import { NgService } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { DialogService } from "@app/services/dialog.service";
import { eAlertType, IPriceItem, IPriceItemBase, IPriceObject, IQuote, IRootScope, ISfdcCpqGetProductsRequest, ISfdcCpqGetProductsResponse, ISfdcCpqGetProductsResponse_Product2, ISfdcCpqPayload, ISfdcCpqPayloadOption } from "@models";
import { KPromise } from "@rules";
import { IMessage } from "@tools";

@NgService({
    token: Token.SfdcService,
    dependencies: [
        Token.$RootScope,
        Token.$Q,
        Token.DialogService,
    ]
})
export class SfdcService {
    constructor(
        private $rootScope: IRootScope,
        private $q: ng.IQService,
        private dialogService: DialogService
    ) {

    }

    /**
     * get's the record passed in through the canvas context
     */
    public record() {
        if (this.$rootScope.isSfdc) return this.canvasRequest().context.environment.record;
    }

    public canvasRequest() {
        if (this.$rootScope.isSfdc) return this.$rootScope.context.sfdcCanvasRequest;
    }

    /**
     * navigates to the salesforce view page of the object with the given id
     */
    public navigateToObject(id: string) {
        Sfdc.canvas.client.publish(this.$rootScope.context.sfdcCanvasRequest.client, {
            name: "navigateToObject",
            payload: id
        });
    }

    public toggleFullscreen() {
        Sfdc.canvas.client.publish(this.canvasRequest().client, {
            name: "toggleFullscreen",
            payload: {}
        });
    }

    /**
     * send a message to the visual force page hosting our canvas
     * @param msg
     */
    public sendMessage(msg: IMessage) {
        if (this.$rootScope.isSfdc) {
            Sfdc.canvas.client.publish(this.canvasRequest().client, {
                name: msg.name,
                payload: msg.data
            });
        }
    }

    /**
     * makes a quote the primary quote in sfdc by calling through the canvas 
     * @param quote
     */
    public makePrimaryQuote(quote: IQuote): ng.IPromise<any> {
        let deferred = this.$q.defer();

        let sr = this.$rootScope.context.sfdcCanvasRequest;
        let url = sr.context.links.restUrl + "sobjects/KBMAX__Quote__c/" + quote.externalId;
        Sfdc.canvas.client.ajax(url, {
            client: sr.client,
            method: "PATCH",
            contentType: "application/json",
            data: JSON.stringify({
                KBMAX__Primary__c: true
            }),
            success: data => {
                if (data.status === 204) {
                    deferred.resolve();
                } else {
                    deferred.reject();
                }
            }
        });

        return deferred.promise;
    }

    /**
     * when integrating with steelbrick, because of their easyXdm setup, we have to wait for the canvas
     * to tell us what the id of the product is.
     */
    public getPayloadFromSfdcCpq(): ng.IPromise<ISfdcCpqPayload> {
        let deferred = this.$q.defer<ISfdcCpqPayload>();
        let sr = this.$rootScope.context.sfdcCanvasRequest;

        Sfdc.canvas.client.subscribe(sr.client, {
            name: "sfdccpq_payload_response",
            onData: (e: ISfdcCpqPayload) => {
                deferred.resolve(e);
            }
        });

        Sfdc.canvas.client.publish(sr.client, {
            name: "sfdccpq_payload_request",
            payload: {}
        });

        return deferred.promise;
    }

    public getProducts(productCodes: string[]): ng.IPromise<ISfdcCpqGetProductsResponse_Product2[]> {
        let deferred = this.$q.defer<ISfdcCpqGetProductsResponse_Product2[]>();
        let sr = this.$rootScope.context.sfdcCanvasRequest;

        Sfdc.canvas.client.subscribe(sr.client, {
            name: "sfdccpq_getproducts_response",
            onData: (e: ISfdcCpqGetProductsResponse) => {
                deferred.resolve(e.products);
            }
        });

        Sfdc.canvas.client.publish(sr.client, {
            name: "sfdccpq_getproducts",
            payload: { productCodes } as ISfdcCpqGetProductsRequest
        });

        return deferred.promise;
    }

    private getColumnOrMetaValue(key: string, priceItem: IPriceItemBase) {
        // find the price column if there is one
        if (priceItem.columns) {
            let colNames = Object.keys(priceItem.columns);
            for (let colName of colNames) {
                if (colName.isEqual(key)) {
                    return priceItem.columns[colName];
                }
            }
        }
        // find the metadata property if there is one
        if (priceItem.metadata) {
            let metaNames = Object.keys(priceItem.metadata);
            for (let metaName of metaNames) {
                if (metaName.isEqual(key)) {
                    return priceItem.metadata[metaName];
                }
            }
        }

        return null;
    }

    protected getConfigurationAttribute(payload: ISfdcCpqPayload, name: string): string {
        for (let att in payload.data.product.configurationAttributes) {
            if (!att.isEqual("attributes") && att.isEqual(name)) {
                return att;
            }
        }
        return null;
    }

    protected addColumnsAndMetadataToConfigurationObject(
        payload: ISfdcCpqPayload,
        configurationData: {},
        item: IPriceItem
    ) {
        for (let col in item.columns) {
            let att = this.getConfigurationAttribute(payload, col);
            if (att) {
                configurationData[att] = item.columns[col];
            }
        }
        for (let m in item.metadata) {
            let att = this.getConfigurationAttribute(payload, m);
            if (att) {
                configurationData[att] = item.metadata[m];
            }
        }
    }

    private dynamicOptionsToQueryStatic: { item: IPriceItem, dynamicOption: ISfdcCpqPayloadOption }[] = [];
    protected buildOptions(payload: ISfdcCpqPayload, item: IPriceItem, optionConfig: { [name: string]: ISfdcCpqPayloadOption[]}, isDynamic: boolean = false) {
        if (!optionConfig) {
            let options: { [name: string]: ISfdcCpqPayloadOption[]};
            if (isDynamic)
                options = { 'Dynamic Options': [] };
            else
                options = { 'Other Options': []};
            optionConfig = options;
        }

        //let dynamicOptionsToQuery: { item: IPriceItem, dynamicOption: ISfdcCpqPayloadOption }[] = [];
        if (isDynamic) {
            
            item.items.forEach((element, index) => {
                let salesforceId = item.columns.salesforceId || item.metadata.salesforceId;
                let dynamicOption = {
                    ProductCode: element.sku,
                    Quantity: element.qty ?? 1,
                    configurationData: {},
                    productId: '',
                    selected: true
                }

                this.addColumnsAndMetadataToConfigurationObject(payload, dynamicOption.configurationData, element);
                // if (element.sku == 'y_product')
                //     dynamicOption.productId = '01t4x000008PckRAAS';
                // if (element.sku == 'z_product')
                //     dynamicOption.productId = '01t4x000008PcknAAC';
                optionConfig['Dynamic Options'][index] = dynamicOption;

                if (!salesforceId) {
                    this.dynamicOptionsToQueryStatic.push({ item: element, dynamicOption });
                }

                if (element.items.length) {
                    let dynOptsBuild = this.buildOptions(payload, element, optionConfig['Dynamic Options'][index].optionConfigurations, true);
                    optionConfig['Dynamic Options'][index].optionConfigurations = dynOptsBuild.load;
                    //this.dynamicOptionsToQueryStatic.push(...dynOptsBuild.dynOptionsToQuery);
                }

            })
        } else {
            item.items.forEach((element, index) => {
                optionConfig['Other Options'][index] = {
                    ProductCode: element.sku,
                    ProductName: Object.keys({element})[0],
                    optionId: element.externalId,
                    selected: true,
                    Quantity: element.qty ?? 1,
                    configurationData: {},
                };
                this.addColumnsAndMetadataToConfigurationObject(payload, optionConfig['Other Options'][index].configurationData, element);
                if (element.items.length > 0) {
                    optionConfig['Other Options'][index].optionConfigurations = this.buildOptions(payload, element, optionConfig['Other Options'][index].optionConfigurations).load;
                }
            });
        }
        
        
        return {load: optionConfig, dynOptionsToQuery: this.dynamicOptionsToQueryStatic};
    }
    protected clearNestedOptions(options: { [name: string]: ISfdcCpqPayloadOption[] }) {
        for (let option in options) {
            options[option].forEach(element => {
                element.selected = false;
                if (element.optionConfigurations) {
                    element.optionConfigurations = this.clearNestedOptions(element.optionConfigurations);
                }
            });
        }
        return options;
    }

    public saveToSfdcCpq(payload: ISfdcCpqPayload, priceObject: IPriceObject) {
        let sr = this.$rootScope.context.sfdcCanvasRequest;
        let payloadOrig = payload;
        // clear out old options
        for (let feature in payload.data.product.optionConfigurations) {
            if (feature.isEqual("Dynamic Options")) {
                payload.data.product.optionConfigurations[feature].forEach(option => {
                    option.Quantity = 0;
                    option.selected = false;
                }); // clear out dynamic options
            } else {
                payload.data.product.optionConfigurations[feature].forEach(option =>
                    option.selected = false); // unselect all pre-defined product options
                // unselect all pre-defined product options
                payload.data.product.optionConfigurations[feature].forEach(option => {
                    option.selected = false;
                    //delete option['optionConfigurations'];
                    //console.log(option.ProductCode, 'cleared');
                    if (option.optionConfigurations) {
                        option.optionConfigurations = this.clearNestedOptions(option.optionConfigurations);
                    }
                    //option.Quantity = 0;
                    
                });
            }
        }

        let dynamicOptionsToQuery: { item: IPriceItem, dynamicOption: ISfdcCpqPayloadOption }[] = [];

        if (priceObject) {
            // first we process non-dynamic product options
            // we recurse through all the price items and try to find matching product 
            // options that are not in the 'Dynamic Options' optionConfigurations object of the payload
            priceObject.items.forEach(item => {
                let foundInOptions = false;
                featureLoop:
                for (let feature in payload.data.product.optionConfigurations) {
                    if (!feature.isEqual("Dynamic Options")) {
                        let options: ISfdcCpqPayloadOption[] = payload.data.product.optionConfigurations[feature];
                        for (let option of options) {
                            if (item.sku == option.ProductCode) {
                                option.selected = true;
                                option.Quantity = item.qty ?? 1;
                                foundInOptions = true;

                                if (item.items.length > 0) {
                                    option.optionConfigurations = this.buildOptions(payload, item, option.optionConfigurations).load;
                                }
                                // load configuration attributes also
                                // we only add configuration attributes that are defined 
                                // in the configurationAttributes section of the json
                                option.configurationData = option.configurationData || {};
                                this.addColumnsAndMetadataToConfigurationObject(
                                    payload, option.configurationData, item);
                                
                                break featureLoop;
                            }
                        }
                    }
                }

                if (!foundInOptions) {
                    // if it's not found in the options, then we try to add it as a dynamic product option:  
                    // tslint:disable-next-line:max-line-length
                    // https://community.steelbrick.com/t5/Developer-Guidebook/Configurator-Integration-to-Third-Party-Web-Apps/ta-p/5575
                    let dynamicFeature = payload.data.product.optionConfigurations["Dynamic Options"];
                    // salesforceId must be defined for dynamic product options to work
                    let salesforceId = item.columns.salesforceId || item.metadata.salesforceId;
                    if (dynamicFeature) {
                        // first see if the dynamic product option is already there
                        //let dynamicOption: ISfdcCpqPayloadOption =
                        //    dynamicFeature.find(dynOpt => dynOpt.ProductCode && dynOpt.ProductCode.isEqual(item.sku));
                        //if (dynamicOption) {
                        //    dynamicOption.Quantity = item.qty;
                        //    dynamicOption.selected = true;
                        //} else {
                        let dynamicOption: ISfdcCpqPayloadOption = {
                            productId: salesforceId,
                            Quantity: item.qty,
                            configurationData: {},
                            selected: true,
                        };
                        

                        if (item.items.length) {
                            let dynOptsBuild = this.buildOptions(payload, item, dynamicOption.optionConfigurations, true);
                            dynamicOption.optionConfigurations = dynOptsBuild.load;
                            //dynamicOptionsToQuery.push(...dynOptsBuild.dynOptionsToQuery);
                        }
                        dynamicFeature.push(dynamicOption);

                        // if the salesforceId wasn't provided directly,
                        // then we make a call later to get the product id using the product code
                        if (!salesforceId) {
                            this.dynamicOptionsToQueryStatic.push({ item, dynamicOption });
                        }
                        this.addColumnsAndMetadataToConfigurationObject(
                            payload,
                            dynamicOption.configurationData,
                            item
                        );

                    }
                }
            });

            // set the product level configuration attributes that correspond to price columns or price item metadata
            if (payload.data.product.configurationAttributes) {
                for (let prop in payload.data.product.configurationAttributes) {
                    let attValue = this.getColumnOrMetaValue(prop, priceObject);
                    if (attValue != null) {
                        // priceObject corresponds to the top level product
                        payload.data.product.configurationAttributes[prop] = attValue;
                    }
                }
            }
        }

        let dynamicOptionsQueryPromise: ng.IPromise<any> = new KPromise(null) as any;
        if (this.dynamicOptionsToQueryStatic.length) {
            dynamicOptionsQueryPromise = this.getProducts(this.dynamicOptionsToQueryStatic.map(o => o.item.sku)
                .removeNulls())
                .then(products => {
                    this.dynamicOptionsToQueryStatic.forEach(dynOption => {
                        let product2 = products.find(p => p.ProductCode.isEqual(dynOption.item.sku));
                        if (!product2) {
                            // tslint:disable-next-line:max-line-length
                            let err = `Unable to add dynamic product option '${dynOption.item.sku}'.  No matching product record was found.`;
                            this.dialogService.alert({ msg: err, persist: true, type: eAlertType.error });
                            throw err;
                        } else {
                            dynOption.dynamicOption.productId = product2.Id;
                        }
                    });
                });
        }

        // DEBUG
        // if (Environment.isDebug) {
        // payload.data.quote["KBMAXSB__Test__c"] = "blah blah";
        // }
        // payload.data.product.optionConfigurations["DynamicFeature"].push(<any>{
        //    productId: "01t41000003vzS3",
        //    Quantity: 2,
        //    configurationData: {
        //        KBMAXSB__Test__c: "hey now",
        //        KBMAXSB__Number_Attribute__c: 444
        //    }
        // });

        // payload.data.product.optionConfigurations["DynamicFeature"].push(<any>{
        //    productId: "01t41000003vzS3",
        //    Quantity: 2,
        //    configurationData: {
        //        KBMAXSB__Test__c: "hey now",
        //        KBMAXSB__Number_Attribute__c: 444
        //    }
        // });

        dynamicOptionsQueryPromise.then(() => {
            Sfdc.canvas.client.publish(sr.client, {
                name: "sfdccpq_save",
                payload
            });
        });

    }
}
